import { z } from "zod";
import { paymentStatusVisibilityPatchSchema, transparencyModePatchSchema } from "./funds.js";

/**
 * Shared DTO — Admin "Cấu hình hiệu lực" per-fund (US-AIF.P0-0244 · @UC9416). READ-ONLY.
 *
 * Trình bày config HIỆU LỰC mà AI form sẽ tuân theo cho 1 quỹ, resolve theo thứ tự
 * fund override > group override > system default. MINH BẠCH (DP-5 no-lying): mỗi field
 * mang KÈM `source` = tầng mà value THỰC SỰ đến từ (không bịa) — admin nhìn được vì sao.
 *
 * P0 chỉ điền field liên quan THU: transparency + reminder (reminder_days_before,
 * overdue_daily_reminder). Vote/threshold/cooling ĐỂ KHUNG `pending` (chưa resolver phase này —
 * KHÔNG bịa giá trị): mảng `framed` liệt kê field sẽ điền ở phase sau.
 *
 * INVARIANTS:
 *  - `.strict()` — chặn field lạ tại biên (không vô tình leak / drift shape).
 *  - source ∈ {fund, group, system} — nguồn tầng thật; KHÔNG có tầng "unknown".
 *  - READ-ONLY: KHÔNG field mutation (override là Phase 4 / task riêng).
 */

/** Tầng mà 1 giá trị config hiệu lực đến từ (minh bạch provenance). */
export const configSourceLayerSchema = z.enum(["fund", "group", "system"]);
export type ConfigSourceLayer = z.infer<typeof configSourceLayerSchema>;

/** 1 field config đã resolve: value hiệu lực + tầng nguồn. */
function resolvedFieldSchema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z
    .object({
      value: valueSchema,
      source: configSourceLayerSchema,
    })
    .strict();
}

/** transparency_mode hiệu lực (public|private) + tầng nguồn. */
export const effectiveTransparencySchema = resolvedFieldSchema(
  transparencyModePatchSchema,
);
export type EffectiveTransparency = z.infer<typeof effectiveTransparencySchema>;

/** reminder_days_before hiệu lực (số ngày int) + tầng nguồn. */
export const effectiveReminderDaysSchema = resolvedFieldSchema(z.number().int());
export type EffectiveReminderDays = z.infer<typeof effectiveReminderDaysSchema>;

/** overdue_daily_reminder hiệu lực (boolean) + tầng nguồn. */
export const effectiveOverdueReminderSchema = resolvedFieldSchema(z.boolean());
export type EffectiveOverdueReminder = z.infer<typeof effectiveOverdueReminderSchema>;

/**
 * Cài đặt THU của quỹ (US-AE4.F7-0909 · @UC9950) — hai khoá đã có engine consumer thật (chống toggle
 * no-op): `self_payment_auto_confirm` (`self-payment-approver.ts`) và `payment_status_visibility`
 * (`payment-board`). Resolve fund > group > system bằng đúng resolver của `FundConfigService`.
 */
export const effectiveCollectionSettingsSchema = z
  .object({
    selfPaymentAutoConfirm: resolvedFieldSchema(z.boolean()),
    paymentStatusVisibility: resolvedFieldSchema(paymentStatusVisibilityPatchSchema),
  })
  .strict();
export type EffectiveCollectionSettings = z.infer<typeof effectiveCollectionSettingsSchema>;

/**
 * 1 khoá luật vote/chi/cooling đã resolve: value hiệu lực (dạng wire) + tầng nguồn.
 * Tiền = CHUỖI BigInt minor (B1 no-float) ⇒ union cho phép string; số nguyên/bool cho khoá phi-tiền.
 */
export const resolvedGovernanceFieldSchema = z
  .object({
    value: z.union([z.string(), z.number(), z.boolean()]),
    source: configSourceLayerSchema,
  })
  .strict();
export type ResolvedGovernanceField = z.infer<typeof resolvedGovernanceFieldSchema>;

/**
 * Khối luật chi/vote/cooling HIỆU LỰC (US-AIF.P4-0258 · @UC9451) — ĐIỀN THẬT (thay khung `pending`).
 * Mỗi field KÈM tầng nguồn ⇒ admin thấy ngay khoá nào đang bị ghi đè ở tầng quỹ.
 * MỖI khoá có engine consumer thật (xem `admin-override-config.ts` — chống toggle no-op).
 */
export const governanceConfigSchema = z
  .object({
    voteWeightSource: resolvedGovernanceFieldSchema,
    voteQuorumMin: resolvedGovernanceFieldSchema,
    coolingThresholdMinor: resolvedGovernanceFieldSchema,
    coolingWindowMinutes: resolvedGovernanceFieldSchema,
    expenseAutoModeEnabled: resolvedGovernanceFieldSchema,
    expenseAutoApproveThresholdMinor: resolvedGovernanceFieldSchema,
    expenseVoteRequiredThresholdMinor: resolvedGovernanceFieldSchema,
  })
  .strict();
export type GovernanceConfig = z.infer<typeof governanceConfigSchema>;

/**
 * Field KHUNG (chưa có resolver ở phase này) — DP-5 no-lying: KHÔNG bịa value, chỉ nêu
 * key + phase dự kiến điền. `key` = nhãn máy (vd "vote_threshold"), `note` = mô tả người đọc.
 */
export const framedConfigFieldSchema = z
  .object({
    key: z.string(),
    note: z.string(),
    status: z.literal("pending"),
  })
  .strict();
export type FramedConfigField = z.infer<typeof framedConfigFieldSchema>;

/**
 * Response GET /admin/funds/:id/effective-config (AC @UC9416).
 * `fundId` echo lại quỹ; nhóm `reminder` gộp 2 key nhắc; `framed` = danh sách field phase sau.
 *
 * US-AIF.P4-0258 (@UC9451): `governance` = luật vote/ngưỡng chi/cooling HIỆU LỰC — ĐIỀN THẬT
 * (mỗi field có resolver + engine consumer). `framed` GIỮ LẠI (mảng có thể rỗng) cho field thực sự
 * chưa có resolver — DP-5 no-lying: khoá đã điền value KHÔNG còn nằm ở `framed`.
 */
export const adminEffectiveConfigResponseSchema = z
  .object({
    fundId: z.string(),
    transparency: effectiveTransparencySchema,
    reminder: z
      .object({
        daysBefore: effectiveReminderDaysSchema,
        overdueDaily: effectiveOverdueReminderSchema,
      })
      .strict(),
    /** Cài đặt thu HIỆU LỰC + tầng nguồn (US-AE4.F7-0909 · @UC9950) — CHỈ XEM ở admin. */
    collection: effectiveCollectionSettingsSchema,
    /** Luật vote/ngưỡng chi/cooling HIỆU LỰC + tầng nguồn (US-AIF.P4-0258 — override được). */
    governance: governanceConfigSchema,
    /** Field CHƯA có resolver — KHUNG (KHÔNG bịa value). Rỗng = không còn field treo. */
    framed: z.array(framedConfigFieldSchema),
  })
  .strict();
export type AdminEffectiveConfigResponse = z.infer<
  typeof adminEffectiveConfigResponseSchema
>;
